import moment from 'moment';
import { formatCurrencyWhole } from '../../utils/formatCurrency';
import { Cell, DayIndicator, DayNumber, DayPrice } from './CalendarDay.styles';
import type { CalendarDayProps } from './CalendarDay.types';

export function CalendarDay({
  day,
  dateString,
  status,
  price,
  currencyCode,
  isCheckIn,
  isCheckOut,
  isInRange,
  isPast,
  isToday,
  isRangeStart,
  isRangeEnd,
  isRowStart,
  isRowEnd,
  isSelectingCheckOut,
  onClick,
  checkInLabel = 'check-in',
  checkOutLabel = 'check-out',
}: CalendarDayProps) {
  if (!day || !dateString) {
    return <Cell data-empty disabled aria-hidden />;
  }

  const isSelected = isCheckIn || isCheckOut;
  const isBlocked =
    isPast ||
    status === 'unavailable' ||
    status === 'hotel_closed' ||
    (status === 'check_in_closed' && !isSelectingCheckOut);

  const showPrice = !isPast && price !== undefined && price > 0 && !!currencyCode;
  const priceText = showPrice ? formatCurrencyWhole(price, currencyCode) : undefined;
  const accessibleName = [
    moment(dateString, 'YYYY-MM-DD').format('dddd D MMMM YYYY'),
    priceText,
    isCheckIn ? checkInLabel : undefined,
    isCheckOut ? checkOutLabel : undefined,
  ]
    .filter(Boolean)
    .join(', ');

  const handleClick = () => {
    if (!isBlocked && onClick && dateString) {
      onClick(dateString);
    }
  };

  const isCheckInClosed =
    status === 'check_in_closed' && !isSelectingCheckOut && !isSelected && !isInRange;

  return (
    <Cell
      {...(isBlocked ? { 'data-blocked': '' } : undefined)}
      {...(isPast ? { 'data-past': '' } : undefined)}
      {...(isSelected ? { 'data-selected': '' } : undefined)}
      {...(isInRange && !isSelected ? { 'data-in-range': '' } : undefined)}
      {...(isRangeStart ? { 'data-range-after': '' } : undefined)}
      {...(isRangeEnd ? { 'data-range-before': '' } : undefined)}
      {...(isRowStart ? { 'data-row-start': '' } : undefined)}
      {...(isRowEnd ? { 'data-row-end': '' } : undefined)}
      {...(isToday && !isSelected && !isInRange ? { 'data-today': '' } : undefined)}
      {...(isCheckInClosed ? { 'data-check-in-closed': '' } : undefined)}
      {...(!isBlocked && !isSelected && day ? { 'data-hoverable': '' } : undefined)}
      onClick={handleClick}
      disabled={isBlocked}
      aria-label={accessibleName}
      data-date={dateString}
      type="button"
    >
      <DayIndicator>
        <DayNumber $selected={isSelected}>{day}</DayNumber>
        {priceText && <DayPrice $selected={isSelected}>{priceText}</DayPrice>}
      </DayIndicator>
    </Cell>
  );
}
